import type { Job, JobEvent } from '@/lib/scraper'
import { FINAL_STATUSES, scraperApi } from '@/lib/scraper'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowUpRight, Loader2, Square } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { BTN_SECONDARY, CARD, MUTED, STATUS_PILL, cls } from './ui'

const EVENT_TEXT: Record<string, (p: Record<string, unknown>) => string> = {
  tier_escalated: (p) =>
    `Escalated from ${p.from}${p.to ? ` to ${p.to}` : ''}: ${p.reason ?? ''}`,
  captcha: (p) =>
    `Captcha ${p.kind}: ${p.solved ? `solved by ${p.solver}` : 'not solved'}`,
  page_done: (p) => `Page done: ${p.url ?? ''} ${p.tier ? `(${p.tier})` : ''}`,
  section_found: (p) => `Found ${p.kind}${p.heading ? `: ${p.heading}` : ''}`,
  extraction_ready: (p) =>
    `${p.recipe} extracted, ${Math.round(Number(p.confidence ?? 0) * 100)}% confidence`,
  job_done: (p) => `Job ${p.status}`,
  basic_info_followup: (p) =>
    `Completing basic info${p.missing ? ` (missing ${(p.missing as string[]).join(', ')})` : ''} from ${((p.pages as string[]) ?? []).length} pages`
}

export function JobProgress({
  job,
  events,
  onOpen
}: { job: Job; events: JobEvent[]; onOpen?: () => void }) {
  const queryClient = useQueryClient()
  const [cancelling, setCancelling] = useState(false)
  const final = FINAL_STATUSES.includes(job.status)
  const percent = job.pages_total
    ? Math.min(100, Math.round((job.pages_done / job.pages_total) * 100))
    : final
      ? 100
      : 5

  const cancel = async () => {
    setCancelling(true)
    try {
      await scraperApi.cancel(job.id)
      toast.success('Cancelling after the current page')
      await queryClient.invalidateQueries({ queryKey: ['scraper-job', job.id] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel')
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div className={cls(CARD, 'space-y-3')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={STATUS_PILL[job.status]}>
          {!final && <Loader2 className="size-3 animate-spin" />}
          {job.status}
        </span>
        <span className="text-sm font-semibold capitalize text-neutral-900 dark:text-white">
          {job.kind}
        </span>
        <span className={cls(MUTED, 'min-w-0 truncate')}>{job.url}</span>
        <div className="ml-auto flex gap-2">
          {!final && (
            <button
              type="button"
              onClick={cancel}
              disabled={cancelling}
              className={BTN_SECONDARY}
            >
              {cancelling ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Square className="size-3.5" />
              )}
              Cancel
            </button>
          )}
          {onOpen && (
            <button type="button" onClick={onOpen} className={BTN_SECONDARY}>
              Open job <ArrowUpRight className="size-3.5" />
            </button>
          )}
        </div>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cls(
            'h-full rounded-full transition-all',
            job.status === 'failed'
              ? 'bg-red-500'
              : 'bg-neutral-900 dark:bg-white'
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className={cls(MUTED, 'flex flex-wrap gap-x-4 gap-y-1')}>
        <span>
          {job.pages_done} / {job.pages_total || '?'} pages
        </span>
        <span>{job.llm_tokens_in + job.llm_tokens_out} LLM tokens</span>
        <span>{job.vision_pages} vision pages</span>
        <span>{job.captcha_solves} captchas solved</span>
      </div>
      {job.error && <p className="text-xs text-red-500">{job.error}</p>}
      {events.length > 0 && (
        <ol
          className="max-h-48 space-y-1 overflow-y-auto border-t border-neutral-200 pt-3 font-mono text-[11px] text-neutral-600 dark:border-neutral-800 dark:text-neutral-400"
          aria-live="polite"
        >
          {events.map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <span className="text-neutral-400">
                {e.at ? new Date(e.at).toLocaleTimeString() : ''}
              </span>{' '}
              {(EVENT_TEXT[e.event] ?? (() => e.event))(e.payload)}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
